import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Download,
  RefreshCw,
} from 'lucide-react';
import { useState, useEffect } from 'react';

import { useToast } from '@/components/ToastProvider';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  useAnalysisRun,
  useAnalysisResults,
  useExportAnalysisResults,
} from '@/hooks/useAnalysis';
import type {
  AnalysisResult,
  RoutineAnalysisResult,
  UserAnalysisResult,
} from '@/types/analysis';

interface AnalysisRunDetailProps {
  runId: string;
  resultsPage: number;
  onResultsPageChange: (page: number) => void;
  onBack: () => void;
}

const STATUS_BADGE_VARIANT = {
  pending: 'secondary',
  running: 'default',
  completed: 'outline',
  failed: 'destructive',
} as const;

export default function AnalysisRunDetail({
  runId,
  resultsPage,
  onResultsPageChange,
  onBack,
}: AnalysisRunDetailProps) {
  const [exporting, setExporting] = useState(false);
  const resultsLimit = 20;

  const { data: run, isLoading, error, refetch } = useAnalysisRun(runId);
  const { data: results } = useAnalysisResults(
    runId,
    resultsPage,
    resultsLimit
  );
  const { exportResults } = useExportAnalysisResults();
  const toast = useToast();

  // Auto-refetch while pending/running
  useEffect(() => {
    if (!run || (run.status !== 'pending' && run.status !== 'running')) return;

    const interval = setInterval(() => refetch(), 3000);
    return () => clearInterval(interval);
  }, [run, refetch]);

  const hasNextResultsPage = results?.length === resultsLimit;

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportResults(runId);
      toast.success('CSV exported');
    } catch (error) {
      toast.error(
        'Export failed',
        error instanceof Error ? error.message : 'Unknown error'
      );
    } finally {
      setExporting(false);
    }
  };

  if (error) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2 mb-4">
            <Button variant="ghost" size="sm" onClick={onBack}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Runs
            </Button>
          </div>
          <CardTitle className="text-destructive">
            Error Loading Analysis Run
          </CardTitle>
          <CardDescription>
            {error instanceof Error
              ? error.message
              : 'An unknown error occurred'}
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (isLoading || !run) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2 mb-4">
            <Button variant="ghost" size="sm" onClick={onBack}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Runs
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const progressPercent =
    run.total_messages > 0
      ? Math.round((run.processed_messages / run.total_messages) * 100)
      : 0;

  const isUserRun = run.target_type === 'users';
  const isRoutineRun = run.target_type === 'routines';
  const summaryEntries = Object.entries(run.summary ?? {})
    .filter((entry): entry is [string, number] => typeof entry[1] === 'number')
    .sort((a, b) => b[1] - a[1]);
  const summaryTotal = summaryEntries.reduce(
    (sum, [, count]) => sum + count,
    0
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2 mb-4">
            <Button variant="ghost" size="sm" onClick={onBack}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Runs
            </Button>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <CardTitle className="text-xl">{run.name}</CardTitle>
                <Badge variant={STATUS_BADGE_VARIANT[run.status]}>
                  {run.status}
                </Badge>
              </div>
              <CardDescription className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                <span>Created by: {run.created_by}</span>
                <span>Model: {run.model}</span>
                <span>{formatDate(run.created_at)}</span>
              </CardDescription>
            </div>
            {run.status === 'completed' && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleExport}
                disabled={exporting}
              >
                <Download className="h-4 w-4 mr-2" />
                {exporting ? 'Exporting...' : 'Export CSV'}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Progress */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Progress</span>
              <span>
                {run.processed_messages} / {run.total_messages} (
                {progressPercent}%)
              </span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div
                className="bg-primary h-2 rounded-full transition-all"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>

          {/* Error */}
          {run.error && (
            <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded-md">
              <p className="font-semibold">
                {run.status === 'failed' ? 'Error' : 'Incomplete'}
              </p>
              <p className="text-sm mt-1">{run.error}</p>
            </div>
          )}

          {/* Prompt */}
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-muted-foreground">
              Prompt
            </h3>
            <pre className="text-sm p-3 bg-muted rounded-lg whitespace-pre-wrap font-mono">
              {run.prompt}
            </pre>
          </div>

          {/* Category distribution */}
          {run.status === 'completed' && summaryEntries.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-muted-foreground">
                Category Distribution
              </h3>
              <div className="space-y-1.5">
                {summaryEntries.map(([category, count]) => (
                  <div key={category} className="flex items-center gap-3">
                    <span className="text-sm w-48 truncate" title={category}>
                      {category}
                    </span>
                    <div className="flex-1 bg-muted rounded-full h-2">
                      <div
                        className="bg-primary h-2 rounded-full"
                        style={{
                          width: `${summaryTotal > 0 ? (count / summaryTotal) * 100 : 0}%`,
                        }}
                      />
                    </div>
                    <span className="text-sm text-muted-foreground w-20 text-right">
                      {count.toLocaleString()} (
                      {summaryTotal > 0
                        ? Math.round((count / summaryTotal) * 100)
                        : 0}
                      %)
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Filters */}
          {run.filters && Object.keys(run.filters).length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-muted-foreground">
                Filters
              </h3>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
                {Object.entries(run.filters).map(([key, value]) => {
                  if (value === undefined || value === null || value === '')
                    return null;
                  return (
                    <div key={key} className="border rounded-md p-2">
                      <p className="text-xs text-muted-foreground">{key}</p>
                      <p className="text-sm font-medium mt-0.5">
                        {Array.isArray(value)
                          ? value.join(', ')
                          : String(value)}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Results Table */}
      {run.status === 'completed' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Results</CardTitle>
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>
                      {isUserRun
                        ? 'Summaries Used'
                        : isRoutineRun
                          ? 'Title'
                          : 'Role'}
                    </TableHead>
                    <TableHead>LLM Output</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {results?.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={4}
                        className="text-center py-8 text-muted-foreground"
                      >
                        No results yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    results?.map((result) => (
                      <TableRow key={result.id}>
                        <TableCell className="text-sm">
                          {result.user_email}
                        </TableCell>
                        <TableCell>
                          {isUserRun ? (
                            <span className="text-sm">
                              {(result as UserAnalysisResult).summaries_used}
                            </span>
                          ) : isRoutineRun ? (
                            <span className="text-sm">
                              {(result as RoutineAnalysisResult).title}
                            </span>
                          ) : (
                            <Badge variant="outline">
                              {(result as AnalysisResult).role}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="max-w-md">
                          <details className="text-xs">
                            <summary className="cursor-pointer text-sm text-muted-foreground hover:text-foreground">
                              View output
                            </summary>
                            <pre className="mt-2 p-2 bg-muted rounded text-xs whitespace-pre-wrap font-mono">
                              {JSON.stringify(result.llm_output, null, 2)}
                            </pre>
                          </details>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDate(result.created_at)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-muted-foreground">
                Page {resultsPage}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    onResultsPageChange(Math.max(1, resultsPage - 1))
                  }
                  disabled={resultsPage === 1}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onResultsPageChange(resultsPage + 1)}
                  disabled={!hasNextResultsPage}
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
